import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, getRelatedComics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComicById(id) : undefined;
  const relatedComics = comic ? getRelatedComics(comic, 4) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h2 className="text-lg font-medium text-foreground-800 mb-2">漫画未找到</h2>
          <p className="text-sm text-foreground-500 mb-4">抱歉，您要找的漫画不存在或已下架</p>
          <Link
            to="/"
            className="px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 喵趣漫画免费在线阅读`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '喵趣漫画网',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.category}漫画`, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          <section className="w-full px-4 md:px-6 py-6">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-shrink-0 w-full sm:w-[280px]">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 喵趣漫画免费全集在线阅读`}
                    title={`${comic.title} - 喵趣漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-xl md:text-3xl font-heading text-foreground-950 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium whitespace-nowrap">
                    {comic.category}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '已完结'
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'bg-accent-100 text-accent-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-xs text-foreground-500">作者：{comic.author}</span>
                </div>

                <div className="flex items-center gap-4 mb-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong className="text-foreground-800">{comic.rating}</strong> 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-file-list-3-line"></i>
                    共 {comic.chapters.length} 话
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-0.5 bg-background-100 rounded-full text-[10px] text-foreground-600 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <div className="flex items-center gap-3">
                  <Link
                    to={`/comic/${comic.id}`}
                    className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                  </Link>
                  <span className="text-xs text-foreground-400">
                    更新至第{comic.chapters.length}话 · {comic.updatedAt}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8">
            <h2 className="text-lg md:text-2xl font-heading text-foreground-950 mb-5">
              章节列表
              <span className="ml-2 text-sm font-normal text-foreground-500">共 {comic.chapters.length} 话</span>
            </h2>
            <div className="bg-background-100 rounded-lg border border-background-200/70 p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {comic.chapters.slice().reverse().map((chapter) => (
                  <Link
                    key={chapter.id}
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-between px-3 py-2 bg-background-50 rounded-md border border-background-200/70 hover:border-primary-300 hover:text-primary-500 transition-all text-xs whitespace-nowrap cursor-pointer"
                  >
                    <span className="truncate">第{chapter.number}话</span>
                    <span className="text-[10px] text-foreground-400 ml-2 flex-shrink-0">{chapter.updatedAt}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="w-full px-4 md:px-6 py-8 mb-6">
              <h2 className="text-lg md:text-2xl font-heading text-foreground-950 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {relatedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}